import React, { useEffect, useState } from 'react';
import StepIndicator from './StepIndicator';
import Step1Schools from './Step1Schools';
import Step2Factors from './Step2Factors';
import Step3Subjective from './Step3Subjective';
import Step4Objective from './Step4Objective';
import Step5Results from './Step5Results';
import SortingHatVerdict from './SortingHatVerdict';
import { getSchoolEstimates } from '../utils/schoolData';

const STORAGE_KEY = 'school-picker-state';

function loadSavedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic sanity check
    if (typeof parsed?.step !== 'number' || !Array.isArray(parsed?.schools)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearSavedState() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export const DEFAULT_FACTORS = [
  {
    id: 'academic',
    name: 'Academic Strength',
    description: 'Quality of your intended program, research opportunities, faculty expertise',
    type: 'subjective',
    weight: 7,
    enabled: true,
  },
  {
    id: 'cost',
    name: 'Cost & Financial Aid',
    description: 'Net cost after aid, expected debt — higher score = more affordable',
    type: 'objective',
    weight: 8,
    enabled: true,
  },
  {
    id: 'location',
    name: 'Location Fit',
    description: 'Geography, urban vs. rural, distance from home, climate',
    type: 'subjective',
    weight: 5,
    enabled: true,
  },
  {
    id: 'culture',
    name: 'Campus Culture & Fit',
    description: 'Overall vibe, community feel, student body personality',
    type: 'subjective',
    weight: 7,
    enabled: true,
  },
  {
    id: 'career',
    name: 'Career Outcomes',
    description: 'Employer reputation, job placement rates, internship pipelines',
    type: 'objective',
    weight: 7,
    enabled: true,
  },
  {
    id: 'prestige',
    name: 'Prestige & Reputation',
    description: 'National rankings, alumni network, brand recognition',
    type: 'objective',
    weight: 5,
    enabled: true,
  },
  {
    id: 'classsize',
    name: 'Class Size & Teaching',
    description: 'Student-faculty ratio, access to professors — higher = smaller classes',
    type: 'objective',
    weight: 6,
    enabled: true,
  },
  {
    id: 'extracurricular',
    name: 'Extracurriculars & Student Life',
    description: 'Clubs, sports, Greek life, activities variety',
    type: 'subjective',
    weight: 5,
    enabled: true,
  },
];

// Parsed once when the module loads — used only for initial useState values.
const _initialSaved = loadSavedState();

export default function CollegeWizard({ title, subtitle, hideHeader, wrapperClass, hpTheme, onHatVerdictModeChange }) {
  const [step, setStep] = useState(_initialSaved?.step ?? 1);
  const [schools, setSchools] = useState(_initialSaved?.schools ?? []);
  const [factors, setFactors] = useState(_initialSaved?.factors ?? DEFAULT_FACTORS);
  const [subjectiveScores, setSubjectiveScores] = useState(_initialSaved?.subjectiveScores ?? {});
  const [objectiveScores, setObjectiveScores] = useState(_initialSaved?.objectiveScores ?? {});
  const [showHatVerdictDetails, setShowHatVerdictDetails] = useState(_initialSaved?.showHatVerdictDetails ?? false);
  const [verdictAnimationDone, setVerdictAnimationDone] = useState(_initialSaved?.verdictAnimationDone ?? false);

  const enabledFactors = factors.filter(f => f.enabled);

  const initializeScores = (schoolList) => {
    const initObj = {};
    const initSubj = {};
    schoolList.forEach(school => {
      initObj[school] = getSchoolEstimates(school);
      initSubj[school] = {};
    });
    setObjectiveScores(initObj);
    setSubjectiveScores(initSubj);
  };

  const calculateFinalScores = () => {
    const totalWeight = enabledFactors.reduce((sum, f) => sum + f.weight, 0);
    return schools.map(school => {
      let total = 0;
      const breakdown = {};
      enabledFactors.forEach(factor => {
        const normWeight = factor.weight / totalWeight;
        const scoreMap = factor.type === 'subjective' ? subjectiveScores : objectiveScores;
        const score = scoreMap[school]?.[factor.id] ?? 5;
        const contribution = normWeight * score;
        breakdown[factor.id] = { score, contribution, normWeight };
        total += contribution;
      });
      return { school, total, breakdown };
    }).sort((a, b) => b.total - a.total);
  };

  const handleRestart = () => {
    clearSavedState();
    setStep(1);
    setSchools([]);
    setFactors(DEFAULT_FACTORS);
    setSubjectiveScores({});
    setObjectiveScores({});
    setShowHatVerdictDetails(false);
    setVerdictAnimationDone(false);
  };

  // Persist state to localStorage whenever anything meaningful changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step,
        schools,
        factors,
        subjectiveScores,
        objectiveScores,
        showHatVerdictDetails,
        verdictAnimationDone,
      }));
    } catch { /* ignore quota / private-browsing errors */ }
  }, [step, schools, factors, subjectiveScores, objectiveScores, showHatVerdictDetails]);

  useEffect(() => {
    if (!onHatVerdictModeChange) return;
    onHatVerdictModeChange(Boolean(hpTheme && step === 5 && !showHatVerdictDetails));
  }, [hpTheme, onHatVerdictModeChange, showHatVerdictDetails, step]);



  const finalResults = step === 5 ? calculateFinalScores() : [];
  const isHatVerdictScene = Boolean(hpTheme && step === 5 && !showHatVerdictDetails && finalResults.length > 0);

  return (
    <div className={`app${wrapperClass ? ` ${wrapperClass}` : ''}`}>
      {!hideHeader && (
        <header className="app-header">
          <div className="header-content">
            <h1>{title ?? '🎓 College Decision Assistant'}</h1>
            <p>{subtitle ?? 'A structured, transparent framework for choosing your school'}</p>
          </div>
        </header>
      )}
      <main className={`app-main${isHatVerdictScene ? ' sh-app-main--verdict' : ''}`}>
        {step === 1 && (
          <Step1Schools
            stepIndicator={<StepIndicator currentStep={step} hpTheme={hpTheme} />}
            schools={schools}
            setSchools={setSchools}
            onNext={(schoolList) => {
              initializeScores(schoolList);
              setStep(2);
            }}
            hpTheme={hpTheme}
          />
        )}
        {step === 2 && (
          <Step2Factors
            stepIndicator={<StepIndicator currentStep={step} hpTheme={hpTheme} />}
            factors={factors}
            setFactors={setFactors}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            hpTheme={hpTheme}
          />
        )}
        {step === 3 && (
          <Step3Subjective
            stepIndicator={<StepIndicator currentStep={step} hpTheme={hpTheme} />}
            schools={schools}
            factors={enabledFactors.filter(f => f.type === 'subjective')}
            scores={subjectiveScores}
            setScores={setSubjectiveScores}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
            hpTheme={hpTheme}
          />
        )}
        {step === 4 && (
          <Step4Objective
            stepIndicator={<StepIndicator currentStep={step} hpTheme={hpTheme} />}
            schools={schools}
            factors={enabledFactors.filter(f => f.type === 'objective')}
            scores={objectiveScores}
            setScores={setObjectiveScores}
            onBack={() => setStep(3)}
            onNext={() => {
              setShowHatVerdictDetails(false);
              setStep(5);
            }}
            hpTheme={hpTheme}
          />
        )}
        {step === 5 && (
          isHatVerdictScene ? (
            <SortingHatVerdict
              school={finalResults[0]?.school}
              onViewDetails={() => {
                setVerdictAnimationDone(true);
                setShowHatVerdictDetails(true);
              }}
              onRestart={handleRestart}
              skipAnimation={verdictAnimationDone}
            />
          ) : (
            <Step5Results
              stepIndicator={<StepIndicator currentStep={step} hpTheme={hpTheme} />}
              schools={schools}
              factors={enabledFactors}
              results={finalResults}
              subjectiveScores={subjectiveScores}
              objectiveScores={objectiveScores}
              onBack={() => {
                setVerdictAnimationDone(false);
                setShowHatVerdictDetails(false);
                setStep(4);
              }}
              onAdjustWeights={() => {
                setVerdictAnimationDone(false);
                setShowHatVerdictDetails(false);
                setStep(2);
              }}
              onRestart={handleRestart}
              onCloseVerdict={hpTheme ? () => setShowHatVerdictDetails(false) : undefined}
              hpTheme={hpTheme}
            />
          )
        )}
      </main>
    </div>
  );
}
